import { useEffect, useState } from "react";
import { Eye, Users, MousePointerClick, Smartphone, Globe, TrendingUp, RefreshCw, Clock, ShieldCheck, Target, Mail, Send } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const API = process.env.REACT_APP_BACKEND_URL;

const Trend = ({ pct }) => {
  if (pct === null || pct === undefined) return <span className="text-[11px] font-bold text-slate-400" data-testid="stat-trend">Ingen jämförelse</span>;
  const up = pct > 0, flat = pct === 0;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-extrabold px-1.5 py-0.5 rounded ${flat ? "bg-slate-100 text-slate-500" : up ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-600"}`} data-testid="stat-trend">
      {flat ? "±0 %" : `${up ? "▲" : "▼"} ${Math.abs(pct)} %`} <span className="font-semibold opacity-70">vs föregående period</span>
    </span>
  );
};

const Stat = ({ icon: Icon, label, value, sub, trend, testid }) => (
  <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</span>
      <Icon size={18} className="text-[#1B6CA8]" />
    </div>
    <div className="font-display text-3xl font-extrabold text-[#0B2545] mt-2">{value}</div>
    {trend !== undefined && <div className="mt-1.5"><Trend pct={trend} /></div>}
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);

const Hours = ({ hours, weekdays }) => {
  const max = Math.max(1, ...hours.map((h) => h.count));
  const wmax = Math.max(1, ...weekdays.map((w) => w.count));
  const peak = hours.reduce((a, b) => (b.count > a.count ? b : a), hours[0]);
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-6" data-testid="analytics-hours">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="font-display font-bold text-[#0B2545]">När besöker man oss?</h3>
        {peak?.count > 0 && <span className="text-xs font-bold text-slate-500">Mest besök kl {String(peak.hour).padStart(2, "0")}–{String(peak.hour + 1).padStart(2, "0")} (svensk tid)</span>}
      </div>
      <div className="mt-4 flex items-end gap-[3px] h-24">
        {hours.map((h) => (
          <div key={h.hour} className="flex-1 flex flex-col items-center justify-end h-full" title={`kl ${h.hour}: ${h.count}`}>
            <div className="w-full rounded-t bg-[#1B6CA8]/80 hover:bg-[#0B2545] transition-colors" style={{ height: `${Math.max(2, (h.count / max) * 100)}%` }} />
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-slate-400 mt-1"><span>00</span><span>06</span><span>12</span><span>18</span><span>23</span></div>
      <div className="mt-5 grid grid-cols-7 gap-2">
        {weekdays.map((w) => (
          <div key={w.day} className="text-center">
            <div className="h-10 flex items-end justify-center"><div className="w-full rounded-t bg-[#ffce3a]" style={{ height: `${Math.max(4, (w.count / wmax) * 100)}%` }} /></div>
            <div className="text-[10px] font-bold text-slate-500 mt-1">{w.day.slice(0, 3)}</div>
            <div className="text-[10px] text-slate-400">{w.count}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Bars = ({ title, items, total, testid }) => (
  <div className="bg-white rounded-2xl border border-slate-100 p-6" data-testid={testid}>
    <h3 className="font-display font-bold text-[#0B2545] mb-4">{title}</h3>
    {items.length === 0 && <p className="text-sm text-slate-400">Ingen data ännu.</p>}
    <ul className="space-y-3">
      {items.map((it) => (
        <li key={it.name}>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-slate-700 truncate pr-3 font-medium">{it.name}</span>
            <span className="text-slate-500 flex-shrink-0">{it.count} <span className="text-slate-300">·</span> {total ? Math.round((it.count / total) * 100) : 0}%</span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full bg-[#0B2545] rounded-full" style={{ width: `${total ? (it.count / total) * 100 : 0}%` }} />
          </div>
        </li>
      ))}
    </ul>
  </div>
);

const Chart = ({ daily }) => {
  const max = Math.max(1, ...daily.map((d) => d.views));
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-6" data-testid="analytics-chart">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-bold text-[#0B2545]">Sidvisningar per dag</h3>
        <span className="text-xs text-slate-400 flex items-center gap-3"><span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#0B2545]" /> Visningar</span><span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#ffce3a]" /> Besökare</span></span>
      </div>
      {daily.length === 0 ? <p className="text-sm text-slate-400">Ingen data ännu.</p> : (
        <div className="flex items-end gap-1 h-40">
          {daily.map((d) => (
            <div key={d.date} className="flex-1 flex flex-col justify-end items-center gap-0.5 group relative" title={`${d.date}: ${d.views} visningar, ${d.visitors} besökare`}>
              <div className="w-full flex items-end gap-px">
                <div className="flex-1 bg-[#0B2545] rounded-t" style={{ height: `${(d.views / max) * 140}px` }} />
                <div className="flex-1 bg-[#ffce3a] rounded-t" style={{ height: `${(d.visitors / max) * 140}px` }} />
              </div>
              <span className="text-[9px] text-slate-400 hidden sm:block">{d.date.slice(5)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function AdminAnalytics({ token, onUnauthorized }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [reports, setReports] = useState([]);
  const { toast } = useToast();

  const loadReports = async () => {
    const res = await fetch(`${API}/api/admin/reports`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) setReports(await res.json());
  };

  const sendReport = async () => {
    setSending(true);
    try {
      const res = await fetch(`${API}/api/admin/reports/send-now`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const r = await res.json();
      toast({ title: "Veckorapport skickad", description: `Till ${r.to}` });
      loadReports();
    } catch {
      toast({ title: "Kunde inte skicka rapporten", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/analytics?days=${days}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) return onUnauthorized();
      setData(await res.json());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [days]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadReports(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <p className="text-slate-400 py-10 text-center">Laddar statistik…</p>;
  const t = data.totals;
  const c = data.conversion;

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex p-1 rounded-xl bg-white border border-slate-200">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`analytics-range-${d}`} className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${days === d ? "bg-[#0B2545] text-white" : "text-slate-600 hover:bg-slate-50"}`}>{d} dagar</button>
          ))}
        </div>
        <button onClick={load} data-testid="analytics-refresh" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-slate-200 text-sm font-semibold text-[#0B2545] hover:bg-slate-50"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Uppdatera</button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Eye} label="Sidvisningar" value={t.pageviews} trend={data.trend?.pageviews} sub={`Idag: ${t.today_views} · föregående period: ${data.trend?.prev_pageviews ?? 0}`} testid="stat-pageviews" />
        <Stat icon={Users} label="Unika besökare" value={t.visitors} trend={data.trend?.visitors} sub={`Idag: ${t.today_visitors} · föregående period: ${data.trend?.prev_visitors ?? 0}`} testid="stat-visitors" />
        <Stat icon={TrendingUp} label="Sessioner" value={t.sessions} trend={data.trend?.sessions} sub={`${t.pages_per_session} sidor/session · ${t.bounce_rate}% avvisning`} testid="stat-sessions" />
        <Stat icon={MousePointerClick} label="Bokningsklick" value={c.booking_clicks} trend={data.trend?.booking_clicks} sub={`${t.clicks} klick totalt på knappar`} testid="stat-clicks" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6" data-testid="analytics-conversion">
        <div className="lg:col-span-1 bg-[#0B2545] text-white rounded-2xl p-6 relative overflow-hidden">
          <Target size={120} className="absolute -right-6 -bottom-6 text-white/5" />
          <div className="text-xs font-bold uppercase tracking-wider text-[#ffce3a] flex items-center gap-2"><Target size={14} /> Konverteringsmål</div>
          <div className="font-display text-5xl font-black mt-3" data-testid="conversion-rate">{c.rate}%</div>
          <p className="text-sm text-slate-300 mt-2 leading-relaxed">
            <span className="font-bold text-white">{c.home_to_booking}</span> av <span className="font-bold text-white">{c.home_sessions}</span> besök på startsidan gick vidare till <span className="font-bold text-[#ffce3a]">Boka online</span>.
          </p>
          <div className="mt-4 pt-4 border-t border-white/10 text-sm text-slate-300">
            Totalt <span className="font-bold text-white">{c.booking_clicks}</span> bokningsklick från <span className="font-bold text-white">{c.booking_sessions}</span> sessioner.
          </div>
        </div>
        <div className="lg:col-span-2">
          <Bars title="Sidor som ger flest bokningsklick" items={c.by_page} total={c.booking_clicks} testid="analytics-booking-pages" />
        </div>
      </div>

      <Chart daily={data.daily} />

      <div className="grid md:grid-cols-2 gap-6">
        <Bars title="Trafikkällor – så hittar de oss" items={data.sources} total={t.pageviews} testid="analytics-sources" />
        <Hours hours={data.hours || []} weekdays={data.weekdays || []} />
        <Bars title={`Länder – var de surfar ifrån${data.geo_coverage ? ` (${data.geo_coverage} % av besöken)` : ""}`} items={data.countries || []} total={t.pageviews} testid="analytics-countries" />
        <Bars title="Städer" items={data.cities || []} total={t.pageviews} testid="analytics-cities" />
        <Bars title="Mest besökta sidor" items={data.pages} total={t.pageviews} testid="analytics-pages" />
        <Bars title="Klick på knappar & länkar" items={data.clicks} total={t.clicks} testid="analytics-clicks" />
        <Bars title="Enheter" items={data.devices} total={t.pageviews} testid="analytics-devices" />
        <Bars title="Webbläsare" items={data.browsers} total={t.pageviews} testid="analytics-browsers" />
        <Bars title="Språk" items={data.languages} total={t.pageviews} testid="analytics-languages" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6" data-testid="analytics-consent">
        <h3 className="font-display font-bold text-[#0B2545] mb-3 flex items-center gap-2"><ShieldCheck size={18} className="text-[#1B6CA8]" /> Cookie-samtycke</h3>
        <div className="flex gap-6 text-sm">
          <span><span className="font-bold text-emerald-600 text-lg">{data.consent.accepted}</span> accepterade statistik</span>
          <span><span className="font-bold text-rose-600 text-lg">{data.consent.declined}</span> endast nödvändiga</span>
        </div>
        <p className="text-xs text-slate-400 mt-2">Besökare som avböjt spåras inte – de syns bara här som antal.</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6" data-testid="analytics-reports">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-display font-bold text-[#0B2545] flex items-center gap-2"><Mail size={18} className="text-[#1B6CA8]" /> Veckorapport via e-post</h3>
          <button onClick={sendReport} disabled={sending} data-testid="send-report-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0B2545] text-white text-sm font-bold hover:bg-[#13315C] disabled:opacity-60 transition-colors">
            <Send size={14} /> {sending ? "Skickar…" : "Skicka rapport nu"}
          </button>
        </div>
        <p className="text-sm text-slate-500 mt-2">Skickas automatiskt varje måndag kl. 07:00 till <span className="font-semibold text-[#0B2545]">info@citytrafikskolauppsala.se</span> med senaste veckans besök, konvertering och klick.</p>
        {reports.length > 0 && (
          <ul className="mt-4 divide-y divide-slate-100 text-sm" data-testid="reports-list">
            {reports.slice(0, 5).map((r) => (
              <li key={r.id} className="py-2 flex flex-wrap justify-between gap-2">
                <span className="text-slate-700">{r.subject}</span>
                <span className="text-slate-400 whitespace-nowrap">{new Date(r.sent_at).toLocaleString("sv-SE", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6" data-testid="analytics-recent">
        <h3 className="font-display font-bold text-[#0B2545] mb-4 flex items-center gap-2"><Clock size={18} className="text-[#1B6CA8]" /> Senaste besök</h3>
        {data.recent.length === 0 && <p className="text-sm text-slate-400">Inga besök registrerade ännu.</p>}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-slate-400">
              <tr><th className="pb-2 pr-4">Tid</th><th className="pb-2 pr-4">Besökare</th><th className="pb-2 pr-4">Enhet</th><th className="pb-2 pr-4">Källa</th><th className="pb-2">Sidor</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.recent.map((r) => (
                <tr key={r.session_id + r.start}>
                  <td className="py-2.5 pr-4 whitespace-nowrap text-slate-600">{new Date(r.start).toLocaleString("sv-SE", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                  <td className="py-2.5 pr-4 font-mono text-xs text-slate-500">#{r.visitor_id}</td>
                  <td className="py-2.5 pr-4 text-slate-600"><span className="inline-flex items-center gap-1.5">{r.device === "desktop" ? <Globe size={14} /> : <Smartphone size={14} />} {r.device} · {r.browser}</span></td>
                  <td className="py-2.5 pr-4 text-slate-600">{r.source}</td>
                  <td className="py-2.5 text-slate-600">{r.pages.join(" → ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
